import { isBrowser } from '../../../utils';
import { AB_TEST_COOKIE_PREFIX, AB_TEST_COOKIE_NAME, AB_TEST_PARAM_PREFIX } from '../../../constants';
/**
 * This function is used to get the parameters from the URL that start with the prefix "abtest-"
 * @param urlSearchParams
 * @returns {Record<string, string>}
 * @example
 * const urlSearchParams = new URLSearchParams(window.location.search);
 * const abTestParams = getParamsWithPrefix(urlSearchParams);
 * console.log(abTestParams);
 * // { "abtest-param1": "value1", "abtest-param2": "value2" }
 */
export const getParamsWithPrefix = (urlSearchParams: URLSearchParams): Record<string, string> => {
  const result: Record<string, string> = {};

  urlSearchParams.forEach((value, key) => {
    if (key.startsWith(AB_TEST_PARAM_PREFIX)) {
      result[key] = value;
    }
  });

  return result;
};

/**
 * This function is used to set the cookies from the query parameters
 * @param searchParamsFiltered
 * @example
 * const searchParamsFiltered = { "abtest-param1": "value1", "abtest-param2": "value2" };
 * setCookieFromQueryParams(searchParamsFiltered);
 */
export const setCookieFromQueryParams = (searchParamsFiltered: Record<string, string>) => {
  if (!isBrowser()) {
    return;
  }

  Object.entries(searchParamsFiltered).forEach(([key, value]) => {
    const keyEscaped = key.replace(AB_TEST_PARAM_PREFIX, '').replace('-', '.');
    const cookieName = `${AB_TEST_COOKIE_NAME}${keyEscaped}`;
    const cookieValue = value;
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    document.cookie = `${cookieName}=${cookieValue}; expires=${expirationDate.toUTCString()}; path=/;`;
  });
};

/**
 * This function is used to remove all cookies that start with the prefix "ONEWEB::"
 * @example
 * removeAllABTestingCookies();
 */
export const removeAllABTestingCookies = () => {
  if (!isBrowser()) {
    return;
  }

  const cookies = document.cookie.split(';');

  cookies.forEach((cookie) => {
    const [cookieName] = cookie.split('=');
    const trimmedCookieName = cookieName.trim();

    if (trimmedCookieName.startsWith(AB_TEST_COOKIE_NAME)) {
      // Delete the cookie by setting its expiry date to a past date
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  });
};

/**
 * This function is used to get all cookies that start with the prefix "ONEWEB::"
 * It returns an object with the cookie names as keys and the cookie values as values.
 * @returns {Record<string, string>}
 * @example
 * const abTestingCookies = getAllABTestingCookies();
 * console.log(abTestingCookies);
 * // { "abtest-param1": "value1", "abtest-param2": "value2" }
 */
export const getAllABTestingCookies = () => {
  if (!isBrowser()) {
    return {};
  }

  const cookies = document.cookie.split(';');
  const abTestingCookies: Record<string, string> = {};

  cookies.forEach((cookie) => {
    const [cookieName, cookieValue] = cookie.split('=');
    const trimmedCookieName = cookieName.trim();
    if (trimmedCookieName.startsWith(AB_TEST_COOKIE_NAME)) {
      const key = trimmedCookieName.replace(AB_TEST_COOKIE_PREFIX, '').replace(/\./g, '-');
      abTestingCookies[key] = decodeURIComponent(cookieValue);
    }
  });

  return abTestingCookies;
};
